import { useEffect } from "react";

/**
 * Feature 6 - Disable Text Selection.
 *
 * Applies `user-select: none` (all vendor prefixes) to the target element and
 * blocks the `selectstart` event. Restores the previous inline styles on
 * cleanup so existing behavior is preserved. When `enabled` is false, no-op.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {() => HTMLElement|null} [opts.getTarget]
 * @param {() => void} [opts.onBlocked]
 */
export function useTextSelectionGuard({ enabled, getTarget, onBlocked } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const target = (getTarget && getTarget()) || document.body;
    if (!target) return undefined;

    // Save existing inline values to restore later.
    const prev = {
      userSelect: target.style.userSelect,
      webkitUserSelect: target.style.webkitUserSelect,
      msUserSelect: target.style.msUserSelect,
      mozUserSelect: target.style.MozUserSelect,
    };

    target.style.userSelect = "none";
    target.style.webkitUserSelect = "none";
    target.style.msUserSelect = "none";
    target.style.MozUserSelect = "none";

    const handler = (e) => {
      // Opt-out: elements inside a container marked with
      // data-allow-text-selection (e.g. the Excel web editor overlay) need
      // selection/caret to work for contentEditable. Skip blocking there.
      if (
        e.target &&
        typeof e.target.closest === "function" &&
        e.target.closest("[data-allow-text-selection]")
      ) {
        return;
      }
      e.preventDefault();
      if (typeof onBlocked === "function") onBlocked();
      return false;
    };

    target.addEventListener("selectstart", handler, false);

    return () => {
      target.removeEventListener("selectstart", handler, false);
      target.style.userSelect = prev.userSelect || "";
      target.style.webkitUserSelect = prev.webkitUserSelect || "";
      target.style.msUserSelect = prev.msUserSelect || "";
      target.style.MozUserSelect = prev.mozUserSelect || "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

export default useTextSelectionGuard;
